import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DateField } from '../dropdown';

import { useCreatePayment } from '../../hooks/useCreatePayment';
import { usePaymentMethods } from '../../hooks/usePaymentMethods';

export interface CheckoutDialogInvoice {
  rsInvoiceId: number;
  rsCustomerId: number;
  number: string;
  balanceDue: number;
}

interface CheckoutDialogProps {
  open: boolean;
  onClose: () => void;
  invoice: CheckoutDialogInvoice;
  onSuccess?: (result: { newBalance: number; amount: number }) => void;
}

const formatMoney = (value: number): string =>
  value.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  });

const todayIsoDate = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export function CheckoutDialog({
  open,
  onClose,
  invoice,
  onSuccess,
}: CheckoutDialogProps): React.ReactElement {
  const { methods, isLoading: methodsLoading, error: methodsError } =
    usePaymentMethods();
  const { phase, result, error: submitError, submit, reset } = useCreatePayment();

  const [methodId, setMethodId] = useState<string>('');
  const [amountStr, setAmountStr] = useState<string>(invoice.balanceDue.toFixed(2));
  const [refNum, setRefNum] = useState<string>('');
  const [paymentDate, setPaymentDate] = useState<string>(todayIsoDate());

  // Reset internal state every time the dialog re-opens or invoice changes.
  useEffect(() => {
    if (open) {
      reset();
      setMethodId('');
      setAmountStr(invoice.balanceDue.toFixed(2));
      setRefNum('');
      setPaymentDate(todayIsoDate());
    }
  }, [open, invoice.rsInvoiceId, invoice.balanceDue, reset]);

  // Auto-close + bubble success once the orchestrator acks.
  useEffect(() => {
    if (phase === 'succeeded' && result) {
      onSuccess?.({ newBalance: result.newBalance, amount: result.amount });
      onClose();
    }
  }, [phase, result, onSuccess, onClose]);

  const selectedMethod = useMemo(
    () => methods.find((m) => m.id === methodId) || null,
    [methods, methodId],
  );

  const parsedAmount = (() => {
    const n = Number(amountStr);
    return Number.isFinite(n) ? n : NaN;
  })();

  const amountError = (() => {
    if (amountStr.trim() === '') return 'Amount is required';
    if (!Number.isFinite(parsedAmount)) return 'Amount must be a number';
    if (parsedAmount <= 0) return 'Amount must be greater than 0';
    if (parsedAmount > invoice.balanceDue + 0.001) {
      return `Amount cannot exceed balance due of ${formatMoney(invoice.balanceDue)}`;
    }
    return null;
  })();

  const today = todayIsoDate();
  const paymentDateError = (() => {
    if (!paymentDate) return 'Payment date is required';
    if (paymentDate > today) return 'Payment date cannot be in the future';
    return null;
  })();

  const isSubmitting = phase === 'submitting' || phase === 'accepted';
  const submitDisabled =
    isSubmitting ||
    methodsLoading ||
    !selectedMethod ||
    amountError !== null ||
    paymentDateError !== null;

  const handleSubmit = () => {
    if (!selectedMethod || amountError || paymentDateError) return;
    submit(
      {
        rsInvoiceId: invoice.rsInvoiceId,
        rsCustomerId: invoice.rsCustomerId,
        paymentMethodName: selectedMethod.name,
        dvPaymentMethodId: selectedMethod.id,
        rsPaymentMethodId: selectedMethod.rsPaymentMethodId ?? undefined,
        amount: parsedAmount,
        refNum: refNum.trim() || undefined,
        paymentDate,
      },
      // The payments:changed event filters by the invoice display number,
      // which the orchestrator broadcasts (cr001_number) — not rsInvoiceId.
      invoice.number,
    );
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Check Out — Invoice #{invoice.number}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Balance due
            </Typography>
            <Typography variant="h6">{formatMoney(invoice.balanceDue)}</Typography>
          </Box>

          {methodsError && (
            <Alert severity="error">Could not load payment methods: {methodsError}</Alert>
          )}

          <FormControl fullWidth disabled={methodsLoading || isSubmitting}>
            <InputLabel id="payment-method-label">Payment method</InputLabel>
            <Select
              labelId="payment-method-label"
              label="Payment method"
              value={methodId}
              onChange={(e) => setMethodId(String(e.target.value))}
            >
              {methods.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DateField
            label="Payment date"
            value={paymentDate || null}
            onChange={(iso) => setPaymentDate(iso ?? '')}
            error={paymentDateError !== null}
            helperText={paymentDateError ?? 'When the payment was received'}
            disabled={isSubmitting}
          />

          <TextField
            label="Amount"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            error={amountError !== null}
            helperText={amountError ?? `Up to ${formatMoney(invoice.balanceDue)}`}
            disabled={isSubmitting}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              inputMode: 'decimal',
            }}
            fullWidth
          />

          <TextField
            label="Reference (optional)"
            value={refNum}
            onChange={(e) => setRefNum(e.target.value)}
            placeholder="Cheque #, EFTPOS reference, etc."
            disabled={isSubmitting}
            fullWidth
          />

          {submitError && <Alert severity="error">{submitError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
        >
          {isSubmitting ? 'Recording…' : 'Record Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
